export type AuthLocale = "en-US" | "ja-JP" | "ko-KR";

const STRINGS: Record<
  AuthLocale,
  {
    emailVerificationPrompt: string;
    emailVerificationContinue: string;
    emailVerificationVerifying: string;
    emailVerificationFailed: string;
    emailVerificationResendFailed: string;
    emailVerificationResendLead: string;
    emailVerificationResendIn: string;
    emailVerificationResend: string;
    googleContinueButton: string;
    passwordResetTitle: string;
    passwordResetDescription: string;
    passwordResetSuccess: string;
    passwordSetupTitle: string;
    passwordSetupDescription: string;
    passwordSetupSuccess: string;
    redirectingToSignIn: string;
    // ===== New keys (2026-05-15) =====
    // Modal titles
    signInTitle: string;
    signUpTitle: string;
    forgotPasswordTitle: string;
    emailVerificationTitle: string;
    otpSendCodeTitle: string;
    otpUseCodeTitle: string;
    // CTAs
    signInCta: string;
    signUpCta: string;
    cancelCta: string;
    sendCodeCta: string;
    sendingCodeCta: string;
    signingInCta: string;
    skipCta: string;
    submittingCta: string;
    backCta: string;
    doneCta: string;
    resetPasswordCta: string;
    // LocalSignin form copy
    emailLabel: string;
    emailOrUsernameLabel: string;
    passwordLabel: string;
    usernameLabel: string;
    privateAccountLabel: string;
    showPasswordLabel: string;
    hidePasswordLabel: string;
    forgotPasswordLink: string;
    signInWithCodeLink: string;
    returningUserPrompt: string;
    newUserPrompt: string;
    // Validation messages
    emailInvalid: string;
    passwordTooShort: string;
    passwordTooLong: string;
    usernameTooShort: string;
    usernameTooLong: string;
    usernameInvalidChars: string;
    usernameStartEndChar: string;
    usernameConsecutiveChar: string;
    usernameNotAllowed: string;
    usernameTaken: string;
    passwordsDoNotMatch: string;
    // Generic auth fallback
    authFailedFallback: string;
    socialAuthCanceled: string;
    // OTP step copy
    otpEmailStepInstruction: string;
    otpCodeStepInstruction: string;
    // Password reset description (separate from setup)
    passwordResetForgotIntro: string;
    passwordResetSentSuccessBody: string;
    passwordResetSpamPrompt: string;
    passwordResetResendLink: string;
    // Agreement copy (split for ICU-style composition without concatenation)
    agreementMessage: string;
    agreementUserAgreementLink: string;
    agreementPrivacyPolicyLink: string;
  }
> = {
  "en-US": {
    emailVerificationPrompt: "Enter the 6-digit code we sent to {email}",
    emailVerificationContinue: "Continue",
    emailVerificationVerifying: "Verifying...",
    emailVerificationFailed: "Failed to verify email",
    emailVerificationResendFailed: "Failed to resend code",
    emailVerificationResendLead: "Didn't get an email?",
    emailVerificationResendIn: "Resend in {time}",
    emailVerificationResend: "Resend",
    googleContinueButton: "Continue with Google",
    passwordResetTitle: "Reset your password",
    passwordResetDescription: "Set a new password.",
    passwordResetSuccess: "Your password has been reset successfully!",
    passwordSetupTitle: "Create your password",
    passwordSetupDescription: "Create a password so you can sign in locally with your email and password.",
    passwordSetupSuccess: "Your password has been created successfully!",
    redirectingToSignIn: "Redirecting you to sign in...",
    signInTitle: "Sign in",
    signUpTitle: "Sign up",
    forgotPasswordTitle: "Reset your password",
    emailVerificationTitle: "Verify your email",
    otpSendCodeTitle: "Get your code",
    otpUseCodeTitle: "Use your code",
    signInCta: "Sign in",
    signUpCta: "Sign up",
    cancelCta: "Cancel",
    sendCodeCta: "Send",
    sendingCodeCta: "Sending...",
    signingInCta: "Signing in...",
    skipCta: "Skip",
    submittingCta: "Submitting...",
    backCta: "Back",
    doneCta: "Done",
    resetPasswordCta: "Reset password",
    emailLabel: "Email",
    emailOrUsernameLabel: "Email or username",
    passwordLabel: "Password",
    usernameLabel: "Username",
    privateAccountLabel: "Private account",
    showPasswordLabel: "Show password",
    hidePasswordLabel: "Hide password",
    forgotPasswordLink: "Forgot password?",
    signInWithCodeLink: "Sign in with code",
    returningUserPrompt: "Returning user?",
    newUserPrompt: "New user?",
    emailInvalid: "Please enter a valid email address.",
    passwordTooShort: "Password must be at least 8 characters long",
    passwordTooLong: "Password must not exceed 100 characters",
    usernameTooShort: "Username must be at least 2 characters long.",
    usernameTooLong: "Username must be 30 characters or fewer.",
    usernameInvalidChars: "Username may not contain special characters other than '.' and '_'",
    usernameStartEndChar: "Username must not start or end with '.' or '_'",
    usernameConsecutiveChar: "Username may not contain consecutive '.' or '_'",
    usernameNotAllowed: "Username is not allowed.",
    usernameTaken: "Username is already taken.",
    passwordsDoNotMatch: "Passwords do not match",
    authFailedFallback: "Failed to authenticate. Please try again.",
    socialAuthCanceled: "User canceled or did not fully authorize Facebook.",
    otpEmailStepInstruction: "Enter the email address or username associated with your account.",
    otpCodeStepInstruction:
      "Enter the 6-digit code we sent to the email address associated with your account. If you have an account but did not receive a code, you may have OTP login disabled for your account.",
    passwordResetForgotIntro:
      "Enter your email address or username and we'll send you a link to reset your password.",
    passwordResetSentSuccessBody:
      "An email with a link to reset your password was sent to the email address associated with your account.",
    passwordResetSpamPrompt: "Don't see the email? Check your spam folder, or",
    passwordResetResendLink: "resend the email",
    agreementMessage:
      "By continuing, you agree to our {userAgreement} and acknowledge that you understand the {privacyPolicy}.",
    agreementUserAgreementLink: "User Agreement",
    agreementPrivacyPolicyLink: "Privacy Policy",
  },
  "ja-JP": {
    emailVerificationPrompt: "{email} に送信した6桁のコードを入力してください",
    emailVerificationContinue: "続ける",
    emailVerificationVerifying: "確認中...",
    emailVerificationFailed: "メールを確認できませんでした",
    emailVerificationResendFailed: "コードを再送信できませんでした",
    emailVerificationResendLead: "メールが届きませんでしたか？",
    emailVerificationResendIn: "{time}後に再送信",
    emailVerificationResend: "再送信",
    googleContinueButton: "Googleで続行",
    passwordResetTitle: "パスワードをリセット",
    passwordResetDescription: "新しいパスワードを設定してください。",
    passwordResetSuccess: "パスワードが正常にリセットされました。",
    passwordSetupTitle: "パスワードを作成",
    passwordSetupDescription: "メールアドレスとパスワードでローカルにサインインできるよう、パスワードを作成してください。",
    passwordSetupSuccess: "パスワードが正常に作成されました。",
    redirectingToSignIn: "サインイン画面に移動しています...",
    signInTitle: "サインイン",
    signUpTitle: "サインアップ",
    forgotPasswordTitle: "パスワードをリセット",
    emailVerificationTitle: "メールアドレスを確認",
    otpSendCodeTitle: "コードを取得",
    otpUseCodeTitle: "コードを入力",
    signInCta: "サインイン",
    signUpCta: "サインアップ",
    cancelCta: "キャンセル",
    sendCodeCta: "送信",
    sendingCodeCta: "送信中...",
    signingInCta: "サインイン中...",
    skipCta: "スキップ",
    submittingCta: "送信中...",
    backCta: "戻る",
    doneCta: "完了",
    resetPasswordCta: "パスワードをリセット",
    emailLabel: "メールアドレス",
    emailOrUsernameLabel: "メールアドレスまたはユーザー名",
    passwordLabel: "パスワード",
    usernameLabel: "ユーザー名",
    privateAccountLabel: "非公開アカウント",
    showPasswordLabel: "パスワードを表示",
    hidePasswordLabel: "パスワードを非表示",
    forgotPasswordLink: "パスワードをお忘れですか？",
    signInWithCodeLink: "コードでサインイン",
    returningUserPrompt: "アカウントをお持ちですか？",
    newUserPrompt: "新規ユーザーですか？",
    emailInvalid: "有効なメールアドレスを入力してください。",
    passwordTooShort: "パスワードは8文字以上で入力してください",
    passwordTooLong: "パスワードは100文字以内で入力してください",
    usernameTooShort: "ユーザー名は2文字以上で入力してください。",
    usernameTooLong: "ユーザー名は30文字以内で入力してください。",
    usernameInvalidChars: "ユーザー名に '.' と '_' 以外の特殊文字は使えません",
    usernameStartEndChar: "ユーザー名の先頭と末尾に '.' や '_' は使えません",
    usernameConsecutiveChar: "ユーザー名に '.' や '_' を連続して使うことはできません",
    usernameNotAllowed: "このユーザー名は使用できません。",
    usernameTaken: "このユーザー名は既に使われています。",
    passwordsDoNotMatch: "パスワードが一致しません",
    authFailedFallback: "認証に失敗しました。もう一度お試しください。",
    socialAuthCanceled: "ユーザーがキャンセルしたか、Facebookの認証を完了しませんでした。",
    otpEmailStepInstruction: "アカウントに紐づくメールアドレスまたはユーザー名を入力してください。",
    otpCodeStepInstruction:
      "アカウントに紐づくメールアドレスに送信した6桁のコードを入力してください。アカウントをお持ちでもコードが届かない場合は、OTPログインが無効になっている可能性があります。",
    passwordResetForgotIntro:
      "メールアドレスまたはユーザー名を入力してください。パスワード再設定のリンクをお送りします。",
    passwordResetSentSuccessBody:
      "パスワード再設定のリンクをアカウントに紐づくメールアドレスへ送信しました。",
    passwordResetSpamPrompt: "メールが見当たらない場合は、迷惑メールフォルダをご確認いただくか、",
    passwordResetResendLink: "メールを再送信",
    agreementMessage:
      "続行することで、{userAgreement} に同意し、{privacyPolicy} を理解したものとみなされます。",
    agreementUserAgreementLink: "利用規約",
    agreementPrivacyPolicyLink: "プライバシーポリシー",
  },
  "ko-KR": {
    emailVerificationPrompt: "{email} 로 보낸 6자리 코드를 입력하세요",
    emailVerificationContinue: "계속",
    emailVerificationVerifying: "확인 중...",
    emailVerificationFailed: "이메일을 확인하지 못했습니다",
    emailVerificationResendFailed: "코드를 다시 보내지 못했습니다",
    emailVerificationResendLead: "이메일을 받지 못하셨나요?",
    emailVerificationResendIn: "{time} 후 다시 보내기",
    emailVerificationResend: "다시 보내기",
    googleContinueButton: "Google로 계속",
    passwordResetTitle: "비밀번호 재설정",
    passwordResetDescription: "새 비밀번호를 설정하세요.",
    passwordResetSuccess: "비밀번호가 성공적으로 재설정되었습니다.",
    passwordSetupTitle: "비밀번호 만들기",
    passwordSetupDescription: "이메일과 비밀번호로 로컬 로그인할 수 있도록 비밀번호를 만드세요.",
    passwordSetupSuccess: "비밀번호가 성공적으로 생성되었습니다.",
    redirectingToSignIn: "로그인 화면으로 이동하는 중...",
    signInTitle: "로그인",
    signUpTitle: "회원가입",
    forgotPasswordTitle: "비밀번호 재설정",
    emailVerificationTitle: "이메일 인증",
    otpSendCodeTitle: "코드 받기",
    otpUseCodeTitle: "코드 입력",
    signInCta: "로그인",
    signUpCta: "회원가입",
    cancelCta: "취소",
    sendCodeCta: "보내기",
    sendingCodeCta: "보내는 중...",
    signingInCta: "로그인 중...",
    skipCta: "건너뛰기",
    submittingCta: "제출 중...",
    backCta: "뒤로",
    doneCta: "완료",
    resetPasswordCta: "비밀번호 재설정",
    emailLabel: "이메일",
    emailOrUsernameLabel: "이메일 또는 사용자 이름",
    passwordLabel: "비밀번호",
    usernameLabel: "사용자 이름",
    privateAccountLabel: "비공개 계정",
    showPasswordLabel: "비밀번호 표시",
    hidePasswordLabel: "비밀번호 숨기기",
    forgotPasswordLink: "비밀번호를 잊으셨나요?",
    signInWithCodeLink: "코드로 로그인",
    returningUserPrompt: "계정이 있으신가요?",
    newUserPrompt: "신규 사용자이신가요?",
    emailInvalid: "올바른 이메일 주소를 입력해 주세요.",
    passwordTooShort: "비밀번호는 8자 이상이어야 합니다",
    passwordTooLong: "비밀번호는 100자를 초과할 수 없습니다",
    usernameTooShort: "사용자 이름은 2자 이상이어야 합니다.",
    usernameTooLong: "사용자 이름은 30자를 초과할 수 없습니다.",
    usernameInvalidChars: "사용자 이름에는 '.'과 '_' 외의 특수 문자를 사용할 수 없습니다",
    usernameStartEndChar: "사용자 이름은 '.'이나 '_'로 시작하거나 끝낼 수 없습니다",
    usernameConsecutiveChar: "사용자 이름에 '.'이나 '_'을 연속으로 사용할 수 없습니다",
    usernameNotAllowed: "허용되지 않는 사용자 이름입니다.",
    usernameTaken: "이미 사용 중인 사용자 이름입니다.",
    passwordsDoNotMatch: "비밀번호가 일치하지 않습니다",
    authFailedFallback: "인증에 실패했습니다. 다시 시도해 주세요.",
    socialAuthCanceled: "사용자가 취소했거나 Facebook 인증을 완료하지 않았습니다.",
    otpEmailStepInstruction: "계정과 연결된 이메일 주소 또는 사용자 이름을 입력하세요.",
    otpCodeStepInstruction:
      "계정과 연결된 이메일로 전송된 6자리 코드를 입력하세요. 계정은 있는데 코드를 받지 못한 경우 OTP 로그인이 비활성화되어 있을 수 있습니다.",
    passwordResetForgotIntro:
      "이메일 주소 또는 사용자 이름을 입력하시면 비밀번호 재설정 링크를 보내드립니다.",
    passwordResetSentSuccessBody:
      "비밀번호 재설정 링크가 계정과 연결된 이메일 주소로 전송되었습니다.",
    passwordResetSpamPrompt: "이메일이 보이지 않으면 스팸 폴더를 확인하시거나",
    passwordResetResendLink: "이메일을 다시 보내기",
    agreementMessage:
      "계속 진행하면 {userAgreement}에 동의하며 {privacyPolicy}를 이해한 것으로 간주됩니다.",
    agreementUserAgreementLink: "이용 약관",
    agreementPrivacyPolicyLink: "개인정보 처리방침",
  },
};

export function resolveAuthLocale(): AuthLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getAuthUiStrings(locale?: AuthLocale): (typeof STRINGS)["en-US"] {
  const key = locale ?? resolveAuthLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
